<script setup lang="ts">
import { computed, ref } from 'vue'
import LockedOption from '@/components/LockedOption.vue'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import { useUserStore } from '@/authentication/user.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { toolkitUrls } from '@/util/links'
import { truncateTextWithEllipsis } from '@/util/strings'
import type { UdlCheckpoint, UdlStrategy } from './udl_index_types'
import SlidePanel from '@/components/SlidePanel.vue'

const PREVIEW_DESCRIPTION_LENGTH = 140

const PRINCIPLE_LABEL_CLASSES: Record<string, string> = {
  Representation: 'bg-udl-representationDark',
  Action: 'bg-udl-expressionDark',
  Engagement: 'bg-udl-engagementDark'
}

const props = withDefaults(
  defineProps<{
    checkpoint: UdlCheckpoint
    principle?: string
    expanded?: boolean
  }>(),
  { principle: '', expanded: true }
)

const emit = defineEmits<{
  'update:expanded': [checkpointRef: string, expanded: boolean]
}>()

const userStore = useUserStore()
const analytics = getAnalyticsTracker()

const previewedStrategy = ref('')

const panelId = computed(() => `udl-checkpoint-${props.checkpoint.standard_long_ref.replace(/\W+/g, '-').toLowerCase()}`)
const principleLabelClass = computed(() => PRINCIPLE_LABEL_CLASSES[props.principle.split(' ')[0]] || '')
const strategies = computed(() => props.checkpoint.adaptations)

function isUnlocked(strategy: UdlStrategy) {
  return userStore.isPremium || Boolean(strategy.demo)
}

function previewId(strategy: UdlStrategy) {
  return `${panelId.value}-preview-${strategy.uuid}`
}

function toggleExpanded() {
  analytics.sendEvent(EVENT_NAMES.UDL_INDEX.TOGGLED_CHECKPOINT, {
    udl_checkpoint: props.checkpoint.udl_checkpoint,
    udl_expanded: !props.expanded
  })
  emit('update:expanded', props.checkpoint.standard_long_ref, !props.expanded)
}

function trackOpenedStrategy(strategy: UdlStrategy) {
  analytics.sendEvent(EVENT_NAMES.UDL_INDEX.OPENED_STRATEGY_LINK, {
    udl_checkpoint: props.checkpoint.udl_checkpoint,
    strategy_slug: strategy.slug
  })
}
</script>

<template>
  <div>
    <h5 class="mt-0 text-text-md font-regular leading-relaxed">
      <span
        v-if="principle"
        class="mr-1 rounded-xs px-1.5 py-0.5 text-text-sm text-white"
        :class="principleLabelClass"
      >
        {{ checkpoint.udl_checkpoint }}
      </span>
      <button
        type="button"
        class="text-left text-gray-900 hover:underline focus:outline-offset-4"
        :aria-expanded="expanded"
        :aria-controls="panelId"
        @click="toggleExpanded"
      >
        <span aria-hidden="true">{{ expanded ? '–' : '+' }}</span>
        {{ checkpoint.udl_checkpoint_name }}
        <span class="text-gray-700">({{ strategies.length }} strategies)</span>
      </button>
    </h5>

    <SlidePanel :expanded="expanded">
      <ul :id="panelId" class="my-4 list-disc pl-6 text-gray-500">
      <li
        v-for="strategy in strategies"
        :key="strategy.uuid"
        class="mb-1"
        :class="{ 'list-none': !isUnlocked(strategy) }"
      >
        <template v-if="isUnlocked(strategy)">
          <a
            :href="toolkitUrls.strategyPage(strategy.slug)"
            target="_blank"
            rel="noopener"
            class="text-blue-400 hover:underline focus:outline-offset-4"
            :aria-describedby="previewedStrategy === strategy.uuid ? previewId(strategy) : undefined"
            @mouseenter="previewedStrategy = strategy.uuid"
            @focus="previewedStrategy = strategy.uuid"
            @mouseleave="previewedStrategy = ''"
            @blur="previewedStrategy = ''"
            @keydown.esc="previewedStrategy = ''"
            @click="trackOpenedStrategy(strategy)"
          >
            {{ strategy.title }}
            <OpensInNewTabIcon />
          </a>
          <div
            v-if="previewedStrategy === strategy.uuid"
            :id="previewId(strategy)"
            class="mt-1 w-64 rounded-xs border border-gray-400 bg-white p-3 text-text-sm text-gray-900 shadow-lib-card"
          >
            <img :src="strategy.image_url" alt="" aria-hidden="true" class="mb-2 max-h-[120px]" />
            {{ truncateTextWithEllipsis(strategy.description, PREVIEW_DESCRIPTION_LENGTH) }}
          </div>
        </template>
        <LockedOption v-else :label="strategy.title" />
      </li>
      </ul>
    </SlidePanel>
  </div>
</template>
